import {
    CellDataAccessorProps,
    ColumnBuilder,
    DataAccessClassNameFunction,
    dataAccessorTimesheetTask,
    dataAccessorWorkingHours
} from "./DataTable";
import {Language} from "../../config/LanguageConfig";
import {TimePeriodDisplay} from "../../components/common/data-display/TimePeriodDisplay";
import {formatDateTime} from "../../helpers/DateTimeHelpers";
import {TextDisplay} from "../../components/common/data-display/TextDisplay";
import {projectTitle} from "../projects/Project";
import {fullName} from "../users/User";
import {Nullable} from "./Nullable";

/**
 * DATA TABLE INITIAL COLUMN DEFINITIONS
 *
 * @see ColumnBuilder
 */

/**
 * boolColumn
 * column of type bool creation function
 *
 * @param colBuilder the column builder instance to use
 * @return ColumnBuilder
 */
export function boolColumn( colBuilder: ColumnBuilder ): ColumnBuilder {
        return colBuilder
            .typeBool()
            .align( 'center' )
}

/**
 * intColumn
 * column of type integer to display integer values in data tables
 */
export function intColumn( colBuilder: ColumnBuilder ): ColumnBuilder {
        return colBuilder
            .align( 'right' )
}


/**
 * moneyColumn
 * column of type bool creation function
 *
 * @param colBuilder the column builder instance to use
 * @return ColumnBuilder
 */
export function moneyColumn( colBuilder: ColumnBuilder ): ColumnBuilder {
        return colBuilder
            .typeMoney()
            .align( 'right' )
}

/**
 * moneyRangeColumn
 * column of type bool creation function
 *
 * @param colBuilder the column builder instance to use
 * @return ColumnBuilder
 */
export function moneyRangeColumn( colBuilder: ColumnBuilder ): ColumnBuilder {
        return colBuilder
            .typeMoney()
            .align( 'right' )
}

/**
 * adds type and standard alignment
 *
 * @param colBuilder
 */
export function stringColumn( colBuilder: ColumnBuilder ): ColumnBuilder {
        return colBuilder.align('center')
}

/**
 * initial column definition project for DataTable component
 */
export function projectColumn(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
        colBuilder
            .withName('project')
            .withHeadline( language.projects.rowProject )
            .setDataAccessor( data  => (
                ( data.rowData.hasOwnProperty('project') )
                ? projectTitle( data.rowData.project )
                 : language.common.internal
            ) );

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }

    return colBuilder.build()
}

export function descriptionColumn(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
    colBuilder
        .withName('description')
        .withHeadline( language.timesheet.form.description )
        .setDataAccessor( data  => {
            return ( data.rowData.hasOwnProperty('description') )
                ? data.rowData.description
                : "";
        } );

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }

        return colBuilder.build()
}

export function stageNameColumn(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
    colBuilder
        .withName('projectStage')
        .withHeadline( language.projects.rowProjectStage )
        .setDataAccessor( data  => {
                return ( data.rowData.hasOwnProperty('projectStage') )
                    ? data.rowData.projectStage.name
                    : "";
        } );

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }

    return colBuilder.build();
}

export function phaseNameColumn(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
     colBuilder
        .withName('projectPhase')
        .withHeadline( language.projects.rowProjectStagePhase )
        .setDataAccessor( data  => {
            return ( data.rowData.hasOwnProperty('projectStagePhase') && data.rowData.projectStagePhase !== null )
                ? data.rowData.projectStagePhase.name
                : "";
        } );

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }


    return colBuilder.build();
}

/**
 * initial column definition project for DataTable component
 */
export function userColumn(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
   colBuilder
        .withName('user')
        .withHeadline( language.employees.title )
        .setDataAccessor( data  =>
            fullName( data.rowData.employee )
        );

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }

    return  colBuilder.build();
}

/**
 * initial column definition hoursOfWork
 *
 * @param colBuilder
 * @param language
 */
export function hoursOfWorkColumn(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
   colBuilder
            .withName('hoursOfWork')
            .withHeadline(language.timesheet.rowHoursOfWork)
            .setDataAccessor(dataAccessorWorkingHours)
            .align('right');

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }

    return colBuilder.build();
}
/**
 * initial column definition workTimes
 *
 * @param colBuilder
 * @param language
 */
export function workTimesColumn(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
    colBuilder
        .typeRenderCell(
            ( data: CellDataAccessorProps ) =>
                <TimePeriodDisplay
                    variant="body2"
                    startAt={ data.rowData.startAt }
                    finishAt={ data.rowData.finishAt }
                    language={ data.language }
                />
        )
        .withName('startAt')
        .withHeadline( language.timesheet.rowTime )
        .align('center');

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }

    return colBuilder.build();
}
/**
 * initial column timesheet task
 *
 * @param colBuilder
 * @param language
 */
export function taskColumn(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
    colBuilder.withName('task')
            .withHeadline(language.timesheet.rowTask)
            .setDataAccessor(dataAccessorTimesheetTask)
            .grow(1.5);

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }

    return colBuilder.build();
}
/**
 * initial column definition timeOfEntry
 *
 * @param colBuilder
 * @param language
 */
export function timeOfEntry(
    colBuilder: ColumnBuilder,
    language: Language,
    classNameAccessor:DataAccessClassNameFunction | undefined = undefined
) {
     colBuilder.withName('timeOfEntry')
            .withHeadline(language.timesheet.timeOfEntry)
            .setDataAccessor((data: CellDataAccessorProps) => formatDateTime(data.rowData.timeOfEntry, data.language))
            .align('center');

    if( classNameAccessor ) {
        colBuilder.withClassName( classNameAccessor );
    }

    return colBuilder.build()
}
/**
 * initial column definition lockStatus
 *
 * @param colBuilder
 * @param language
 */
export function lockStatusColumn( colBuilder: ColumnBuilder, language: Language ) {
        return colBuilder.typeLock()
            .withName('lockStatus')
            .withHeadline(language.common.lockStatusTitle.buttonText)
            .align('center')
            .grow(0.5)
            .build()
}